import { useState } from 'react';
import { Loader2, Plus, RotateCcw, X } from 'lucide-react';
import { warehouseApi, apiError } from '../shared/warehouseApi';

const deliveryId = pkg => pkg.is_provincia ? pkg.delivery_point_provincia_id : pkg.delivery_point_id;

export function ReturnIdPicker({ draft, onAdd, onRemove, onPrepare }) {
  const [deliveryType, setDeliveryType] = useState('lima');
  const [id, setId] = useState('');
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const storeCount = new Set(draft.map(pkg => pkg.store_user?.id)).size;

  const add = pkg => {
    const message = onAdd(pkg);
    if (message) {
      setError(message);
      return;
    }
    setId('');
    setMatches([]);
    setError('');
  };

  const lookup = async event => {
    event.preventDefault();
    setError('');
    setMatches([]);
    const normalized = id.trim();
    if (!/^[0-9]+$/.test(normalized) || !Number.isSafeInteger(Number(normalized)) || Number(normalized) < 1) {
      setError('Ingresa un ID de entrega positivo.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await warehouseApi.get('/api/admin/packages/by-delivery-id', {
        params: { delivery_type: deliveryType, delivery_point_id: normalized },
      });
      if (data.packages.length === 1) add(data.packages[0]);
      else setMatches(data.packages);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return <section className="border-t border-gray-100 p-3 sm:p-4" aria-label="Preparar devolución por ID">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div>
        <h3 className="font-montserrat text-sm font-bold text-gray-800">Agregar devolución por ID de entrega</h3>
        <p className="text-xs text-gray-500">La entrada no cambia el estado. El lote se crea al confirmar.</p>
      </div>
      <span className="rounded-full bg-brand-primary/10 px-2 py-1 text-xs font-bold text-brand-primary">{draft.length}/100</span>
    </div>
    <form onSubmit={lookup} className="mt-3 flex min-w-0 flex-wrap items-end gap-2">
      <label className="min-w-[115px] flex-1 space-y-1 text-xs font-semibold text-gray-600 sm:flex-none">
        Tipo de entrega
        <select value={deliveryType} disabled={loading} onChange={event => { setDeliveryType(event.target.value); setMatches([]); setError(''); }}
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800">
          <option value="lima">Lima</option><option value="provincia">Provincia</option>
        </select>
      </label>
      <label className="min-w-[140px] flex-[2] space-y-1 text-xs font-semibold text-gray-600 sm:max-w-xs">
        DeliveryPoint ID
        <input aria-label="DeliveryPoint ID" value={id} disabled={loading} inputMode="numeric" autoComplete="off"
          onChange={event => { setId(event.target.value); setMatches([]); setError(''); }} placeholder="Ej. 836"
          className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-800" />
      </label>
      <button type="submit" disabled={loading || draft.length >= 100} className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-brand-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
        {loading ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Buscar y agregar
      </button>
    </form>
    {error && <p role="alert" className="mt-2 rounded-lg bg-red-50 p-2 text-sm text-red-700">{error}</p>}
    {matches.length > 1 && <div className="mt-3 space-y-2" aria-label="Paquetes de esta entrega">
      <p className="text-xs font-semibold text-gray-600">Esta entrega tiene varios paquetes. Elige el correcto:</p>
      {matches.map(pkg => <div key={pkg.id} className="flex min-w-0 flex-wrap items-center justify-between gap-2 rounded-lg border p-2 text-sm">
        <span className="min-w-0 break-words">#{deliveryId(pkg)} · {pkg.tracking_code} · {pkg.recipient_name || 'Sin destinatario'} · {pkg.store_user?.username || 'Sin tienda'}</span>
        <button type="button" onClick={() => add(pkg)} disabled={pkg.in_pending_return_batch || draft.some(item => item.id === pkg.id)}
          className="rounded-lg border px-3 py-1.5 font-semibold text-brand-primary disabled:opacity-50">
          {pkg.in_pending_return_batch ? 'En lote pendiente' : draft.some(item => item.id === pkg.id) ? 'Agregado' : 'Agregar'}
        </button>
      </div>)}
    </div>}
    {draft.length > 0 && <div className="mt-4 border-t border-gray-100 pt-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-bold text-gray-800">Lista para devolver · {storeCount > 1
          ? `${storeCount} tiendas · Proveedor #${draft[0].store_user?.provider_id}`
          : draft[0].store_user?.username}</p>
        <button type="button" onClick={onPrepare} className="inline-flex items-center gap-2 rounded-lg bg-brand-primary px-3 py-2 text-sm font-semibold text-white">
          <RotateCcw size={16} /> Preparar lote ({draft.length})
        </button>
      </div>
      <ul className="mt-2 grid min-w-0 gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {draft.map(pkg => <li key={pkg.id} className="flex min-w-0 items-start justify-between gap-2 rounded-lg border border-gray-200 p-2 text-xs">
          <span className="min-w-0 break-words"><strong>#{deliveryId(pkg)} {pkg.is_provincia ? '· Provincia' : '· Lima'}</strong><br />{pkg.tracking_code} · {pkg.recipient_name || 'Sin destinatario'}<br /><span className="text-gray-500">{pkg.store_user?.username || 'Sin tienda'} · {pkg.status === 'returned' ? 'Ya Devuelto' : pkg.status}</span></span>
          <button type="button" onClick={() => onRemove(pkg.id)} aria-label={`Quitar paquete ${pkg.tracking_code}`} className="shrink-0 rounded p-1 text-gray-500 hover:bg-gray-100"><X size={16} /></button>
        </li>)}
      </ul>
    </div>}
  </section>;
}
